// src/GalleryPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Loader, X, Camera, Image, Grid, Filter, CheckCircle, Eye } from 'lucide-react';
import { API } from './utils/database-frontend.js';

const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [filteredGallery, setFilteredGallery] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const statsRef = useRef(null);
  const galleryRef = useRef(null);
  
  const isStatsInView = useInView(statsRef, { once: true, threshold: 0.2 });
  const isGalleryInView = useInView(galleryRef, { once: true, threshold: 0.1 });

  const stats = [
    {
      icon: Camera,
      title: "Toplam Fotoğraf",
      count: "200+",
      description: "Proje ve ekipman görselleri",
      gradient: "from-brand-orange to-orange-500"
    },
    {
      icon: Grid,
      title: "Kategori",
      count: "8+",
      description: "Farklı proje kategorileri",
      gradient: "from-brand-navy to-blue-600"
    },
    {
      icon: Image,
      title: "HD Kalite",
      count: "%100",
      description: "Yüksek çözünürlük görseller",
      gradient: "from-brand-gray to-gray-600"
    },
    {
      icon: Eye,
      title: "Detay Görüntüleme",
      count: "Tam Ekran",
      description: "Büyütülmüş görüntüleme",
      gradient: "from-brand-orange to-red-500"
    }
  ];

  // Fetch gallery and categories from database
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Gallery'yi önce yükle - kategoriler başarısız olsa bile gallery gösterilsin
        console.log('🖼️ Galeri yükleniyor...');
        const fetchedGallery = await API.gallery.getAll();
        console.log('Fetched gallery from API:', fetchedGallery);
        console.log('✅ Galeri yüklendi:', fetchedGallery?.length || 0, 'görsel');
        
        // Eğer API yanıt verdi ama veri yoksa, boş array kullan
        const galleryData = Array.isArray(fetchedGallery) ? fetchedGallery : [];
        setGallery(galleryData);
        setFilteredGallery(galleryData);
        setError(null);
        
        // Eğer galeri boşsa kullanıcıya bilgi ver
        if (galleryData.length === 0) {
          console.log('ℹ️ Galeri boş - henüz fotoğraf eklenmemiş');
        }
        
        // Kategorileri ayrı olarak yükle - başarısız olursa sadece kategori filtresi gözükmez
        try {
          console.log('📂 Kategoriler yükleniyor...');
          const fetchedCategories = await API.gallery.getCategories();
          console.log('✅ Kategoriler yüklendi:', fetchedCategories);
          setCategories(['all', ...fetchedCategories]);
        } catch (categoryError) {
          console.warn('⚠️ Kategoriler yüklenemedi:', categoryError);
          // Sadece 'all' kategorisini bırak, filtreleme çalışmaya devam etsin
          setCategories(['all']);
        }
        
      } catch (err) {
        console.error('❌ Galeri yükleme hatası:', err);
        
        // Eğer sadece network/server hatası ise kullanıcıya farklı mesaj göster
        if (err.message?.includes('fetch') || err.message?.includes('NetworkError') || err.message?.includes('500')) {
          setError('Sunucu ile bağlantı kurulamadı. Lütfen backend sunucusunun çalıştığından emin olun.');
        } else {
          setError('Galeri yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        }
        
        setGallery([]);
        setFilteredGallery([]);
        setCategories(['all']);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter gallery by category
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredGallery(gallery);
    } else {
      setFilteredGallery(gallery.filter(item => item.category === selectedCategory));
    }
  }, [selectedCategory, gallery]);

  const openModal = (image) => {
    setSelectedImage(image);
  };

  const closeModal = () => {
    setSelectedImage(null);
  };

  const formatCategoryName = (category) => {
    if (category === 'all') return 'Tümü';
    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getImageHeight = () => {
    // Tüm görseller aynı yükseklikte olsun - düzenli grid için
    return 'h-64';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-brand-navy via-brand-gray to-brand-navy text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-40"></div>
        
        {/* Animated Background Shapes */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.4, 1]
          }}
          transition={{ 
            duration: 32,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 right-20 w-52 h-52 bg-brand-orange opacity-10 rounded-full"
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 26,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute bottom-20 left-20 w-38 h-38 bg-white opacity-5 rounded-full"
        />

        <div className="relative z-10 text-center px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-8"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-brand-orange rounded-full mb-6">
              <Camera className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
          >
            Galeri
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            <span className="text-brand-orange font-semibold">Projelerimizden ve ekipmanlarımızdan</span> 
            özenle seçilmiş kareler.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="flex flex-wrap justify-center gap-4"
          >
            <div className="flex items-center bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-brand-orange mr-2" />
              <span className="text-white">HD Kalite</span>
            </div>
            <div className="flex items-center bg-white bg-opacity-10 backdrop-blur-sm rounded-full px-6 py-3">
              <CheckCircle className="w-5 h-5 text-brand-orange mr-2" />
              <span className="text-white">Kategorili Görseller</span>
            </div>
          </motion.div>
        </div>
      </section>



      {/* Gallery Section */}
      <section ref={galleryRef} className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={isGalleryInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-brand-navy mb-4">Fotoğraf Galerisi</h2>
            <p className="text-xl text-brand-gray max-w-2xl mx-auto">
              Projelerimizin en güzel anlarını keşfedin
            </p>
          </motion.div>

          {/* Category Filter */}
          {categories.length > 1 && (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={isGalleryInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="flex flex-wrap justify-center gap-3 mb-12"
            >
              <div className="flex items-center bg-brand-orange text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Filter className="w-4 h-4 mr-2" />
                <span>Kategoriler:</span>
              </div>
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={isGalleryInView ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-sm font-medium transition duration-300 ${
                    selectedCategory === category
                      ? 'bg-brand-orange text-white shadow-lg'
                      : 'bg-white text-brand-gray border border-gray-200 hover:border-brand-orange hover:text-brand-orange'
                  }`}
                >
                  {formatCategoryName(category)}
                </motion.button>
              ))}
            </motion.div>
          )}

          {/* Loading State */}
          {loading && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative">
                <Loader className="w-12 h-12 text-brand-orange animate-spin" />
                <div className="absolute inset-0 w-12 h-12 border-2 border-brand-orange border-t-transparent rounded-full animate-ping"></div>
              </div>
              <span className="mt-4 text-brand-gray text-lg font-medium">Galeri yükleniyor...</span>
              <p className="text-brand-gray text-sm mt-2">Fotoğrafları getiriyoruz</p>
            </motion.div>
          )}

          {/* Error State */}
          {error && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-20"
            >
              <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-2xl p-8 max-w-md mx-auto">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-red-800 mb-2">Bir Hata Oluştu</h3>
                <p className="text-red-600">{error}</p>
              </div>
            </motion.div>
          )}

          {/* Gallery Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {filteredGallery.length === 0 ? (
                                  <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="col-span-full text-center py-20"
                  >
                  <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-2xl p-12 max-w-md mx-auto">
                    <Camera className="w-16 h-16 text-brand-gray mx-auto mb-4 opacity-50" />
                    <h3 className="text-xl font-semibold text-brand-gray mb-2">
                      {selectedCategory === 'all' ? 'Henüz Fotoğraf Yok' : 'Bu Kategoride Fotoğraf Yok'}
                    </h3>

                  </div>
                </motion.div>
              ) : (
                filteredGallery.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={isGalleryInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                    transition={{ delay: index * 0.05, duration: 0.6 }}
                    whileHover={{ y: -2, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer group border border-gray-100"
                    onClick={() => openModal(item)}
                  >
                    <div className="relative overflow-hidden">
                      <img 
                        src={item.imageUrl || item.image_url || 'https://placehold.co/400x300/f4712f/ffffff?text=Galeri'} 
                        alt={item.alt_text || `Galeri ${item.id}`}
                        className={`w-full ${getImageHeight()} object-contain bg-gradient-to-br from-gray-50 to-gray-100 group-hover:scale-105 transition-transform duration-500`}
                        onError={(e) => {
                          e.target.src = 'https://placehold.co/400x300/f4712f/ffffff?text=Görsel+Bulunamadı';
                        }}
                      />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 flex items-center justify-center">
                        <motion.div
                          initial={{ opacity: 0, scale: 0.5 }}
                          whileHover={{ opacity: 1, scale: 1 }}
                          className="bg-white rounded-full p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        >
                          <Eye className="w-6 h-6 text-brand-orange" />
                        </motion.div>
                      </div>
                      
                      {/* Category Badge */}
                      {item.category && (
                        <div className="absolute top-3 left-3 bg-brand-orange text-white px-2 py-1 rounded-full text-xs font-medium">
                          {formatCategoryName(item.category)}
                        </div>
                      )}
                      
                      {/* Featured Badge */}
                      {item.is_featured && (
                        <div className="absolute top-3 right-3 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                          ⭐ Öne Çıkan
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>
      </section>

      {/* Gallery Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="relative max-w-6xl max-h-[90vh] w-full h-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img 
                src={selectedImage.imageUrl || selectedImage.image_url || 'https://placehold.co/800x600/f4712f/ffffff?text=Galeri'} 
                alt={selectedImage.alt_text || `Galeri ${selectedImage.id}`}
                className="w-full h-full object-contain rounded-2xl"
                onError={(e) => {
                  e.target.src = 'https://placehold.co/800x600/f4712f/ffffff?text=Görsel+Bulunamadı';
                }}
              />
              
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-white bg-opacity-20 backdrop-blur rounded-full p-3 hover:bg-opacity-30 transition duration-300"
              >
                <X className="w-6 h-6 text-white" />
              </button>
              
              {selectedImage.category && (
                <div className="absolute bottom-4 left-4 bg-black bg-opacity-60 backdrop-blur rounded-2xl px-6 py-3">
                  <span className="text-white text-lg font-semibold uppercase">{formatCategoryName(selectedImage.category)}</span>
                  {selectedImage.is_featured && (
                    <span className="text-yellow-400 text-sm ml-2">⭐ Öne Çıkan</span>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-orange to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold mb-6">Projelerinizi Bizimle Gerçekleştirin</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-orange-100">
              Galerimizde gördüğünüz kalitede projeler için bizimle iletişime geçin.
            </p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="/contact"
                className="inline-block bg-white text-brand-orange font-bold py-4 px-12 rounded-2xl transition duration-300 shadow-lg hover:shadow-xl"
              >
                Proje Başlat
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;